import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { DestroyableBase } from 'osn-shared';
import { takeUntil } from 'rxjs/operators';
import { Country } from '../../../domain/country';
import { Platform } from '../../../domain/platform';
import { UserSegment } from '../../../domain/user-segment';
import { ContentPageDetailDialogDataViewModel } from '../../../shared/components/content-page-detail-dialog/content-page-detail-dialog-data-view.model';
import { ContentPageDetailDialogModalViewModel } from '../../../shared/components/content-page-detail-dialog/content-page-detail-dialog-modal-view.model';
import { ContentPageDetailDialogComponent } from '../../../shared/components/content-page-detail-dialog/content-page-detail-dialog.component';
import { PageDialogVariant } from '../../../shared/components/page-detail-dialog/page-dialog-variant';
import { RedeemPageDetailDialogDataViewModel } from '../../../shared/components/redeem-page-detail-dialog/redeem-page-detail-dialog-data.view-model';
import { RedeemPageDetailDialogModalViewModel } from '../../../shared/components/redeem-page-detail-dialog/redeem-page-detail-dialog-modal.view-model';
import { RedeemPageDetailDialogComponent } from '../../../shared/components/redeem-page-detail-dialog/redeem-page-detail-dialog.component';
import { updateCxbPageVersion, updateCxbRedeemPageVersion } from '../../page-edit-screen.actions';
import { AppState } from '../../page-edit-screen.reducer';
import { PageType } from '../../page-type';
import { PageEditScreenViewModel } from './page-edit-screen.view-model';

export abstract class PageEditDialogService extends DestroyableBase {
  protected constructor(protected readonly dialogService: MatDialog, protected readonly store: Store<AppState>) {
    super();
  }

  public abstract openEditMetaDialog(
    viewModel: PageEditScreenViewModel,
    readonly: boolean,
    pageType: PageType,
    selectedCountries?: Country[],
    selectedUserSegments?: UserSegment[],
    selectedPlatforms?: Platform[]
  );
}

@Injectable()
export class ContentPageEditDialogService extends PageEditDialogService {
  constructor(dialogService: MatDialog, store: Store<AppState>) {
    super(dialogService, store);
  }

  public openEditMetaDialog(viewModel: PageEditScreenViewModel, readonly: boolean, pageType: PageType) {
    const pageVersionId = readonly ? undefined : viewModel.draftPageVersionId;

    this.dialogService
      .open<ContentPageDetailDialogComponent, ContentPageDetailDialogModalViewModel>(ContentPageDetailDialogComponent, {
        data: {
          variant: readonly ? PageDialogVariant.READ_ONLY : PageDialogVariant.EDIT,
          isGenrePage: pageType === PageType.genre,
          data: {
            metaTags: viewModel.metaTags,
            tabTitleEnglish: viewModel.titleEnglish,
            tabTitleArabic: viewModel.titleArabic,
            internalName: viewModel.internalName,
            attachedGenre: viewModel.attachedGenre,
            SEOTitleEnglish: viewModel.SEOTitle?.en ?? '',
            SEOTitleArabic: viewModel.SEOTitle?.ar ?? '',
            SEODescriptionEnglish: viewModel.SEODescription?.en ?? '',
            SEODescriptionArabic: viewModel.SEODescription?.ar ?? '',
            isLandingPage: pageType.isLanding,
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      // eslint-disable-next-line import/no-deprecated
      .subscribe((value?: ContentPageDetailDialogDataViewModel) => {
        if (!value) {
          return;
        }
        this.store.dispatch(
          updateCxbPageVersion.sourceAction({
            pageVersion: {
              id: pageVersionId,
              metaTags: value.metaTags,
              internalName: value.internalName,
              titleEnglish: value.tabTitleEnglish,
              titleArabic: value.tabTitleArabic,
              seoTitle: { en: value.SEOTitleEnglish, ar: value.SEOTitleArabic },
              seoDescription: { en: value.SEODescriptionEnglish, ar: value.SEODescriptionArabic },
            },
            pageType,
          })
        );
      });
  }
}

@Injectable()
export class RedeemPageEditDialogService extends PageEditDialogService {
  constructor(dialogService: MatDialog, store: Store<AppState>) {
    super(dialogService, store);
  }

  public openEditMetaDialog(
    viewModel: PageEditScreenViewModel,
    readonly: boolean,
    pageType: PageType,
    selectedCountries: Country[],
    selectedUserSegments: UserSegment[],
    selectedPlatforms: Platform[]
  ) {
    const pageVersionId = readonly ? undefined : viewModel.draftPageVersionId;

    this.dialogService
      .open<RedeemPageDetailDialogComponent, RedeemPageDetailDialogModalViewModel>(RedeemPageDetailDialogComponent, {
        data: {
          variant: readonly ? PageDialogVariant.READ_ONLY : PageDialogVariant.EDIT,
          isGenrePage: pageType === PageType.genre,
          data: {
            metaTags: viewModel.metaTags ?? {},
            tabTitleEnglish: viewModel.titleEnglish,
            tabTitleArabic: viewModel.titleArabic,
            internalName: viewModel.internalName,
            pageUrl: viewModel.pageUrl,
            countryIds: selectedCountries.map((country) => country.id),
            userSegmentIds: selectedUserSegments.map((userSegment) => userSegment.id),
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      // eslint-disable-next-line import/no-deprecated,sonarjs/no-identical-functions
      .subscribe((value?: RedeemPageDetailDialogDataViewModel) => {
        if (!value) {
          return;
        }
        this.store.dispatch(
          updateCxbRedeemPageVersion.sourceAction({
            pageVersion: {
              id: pageVersionId,
              metaTags: value.metaTags,
              internalName: value.internalName,
              titleEnglish: value.tabTitleEnglish,
              titleArabic: value.tabTitleArabic,
            },
            userSegmentIds: value.userSegmentIds,
            countryIds: value.countryIds,
          })
        );
      });
  }
}
